import React, { useEffect, useState } from 'react'

import { Stack, TextField, Typography } from '@mui/material'

import { ErrorCode, type IAuthChallengeFactorOption, type IAuthChallengeState } from '@repo/types'
import { Modal, Button, FieldLabel } from '@repo/ui'
import { CodeInput } from 'components/input'
import { workosAuthService } from 'services/workos-auth.service'
import { getErrorMessage } from 'utils'

import { useStartStepUpMutation, useVerifyStepUpMutation, type StepUpChallenge } from 'hooks/api/useAuthFactors'

interface StepUpModalProps {
  open: boolean
  onClose: () => void
  /** Called once the existing factor is confirmed; the caller then starts the enrollment. */
  onVerified: () => void
}

const CODE_LENGTH = 6

/**
 * "Confirm it's you" with an EXISTING factor before adding another one. Without this, anyone
 * holding a stolen access token could enroll their own phone — and adding a factor also
 * signs out every other device, so they would lock the owner out at the same time.
 *
 * Accepts the factor's code or a recovery code (the API takes either on the same field).
 *
 * SMS opt-out: if the factor's number replied STOP, no code was texted (`smsOptedOut` on the
 * challenge). The modal then says why, and offers the other enrolled method, a recovery code,
 * or "Try again" once the person has texted START — the same ticket endpoints the sign-in
 * challenge uses.
 */
export const StepUpModal = ({ open, onClose, onVerified }: StepUpModalProps) => {
  const { startStepUpAsync, isStartingStepUp } = useStartStepUpMutation()
  const { verifyStepUpAsync, isVerifyingStepUp } = useVerifyStepUpMutation()
  const [challenge, setChallenge] = useState<StepUpChallenge | null>(null)
  const [code, setCode] = useState('')
  const [useRecoveryCode, setUseRecoveryCode] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [alternatives, setAlternatives] = useState<IAuthChallengeFactorOption[]>([])
  const [isRetrying, setIsRetrying] = useState(false)

  const applyState = (state: IAuthChallengeState) => {
    setChallenge((current) =>
      current
        ? { ...current, factorType: state.factorType, maskedPhone: state.maskedPhone, smsOptedOut: state.smsOptedOut }
        : current,
    )
    setAlternatives(state.options.filter((option) => option.factorId !== state.factorId))
  }

  // Only a blocked SMS step-up needs the alternatives list; read it then.
  useEffect(() => {
    if (!challenge?.smsOptedOut) return
    workosAuthService.getChallengeOptions(challenge.ticket).then((state) => state && applyState(state))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [challenge?.ticket, Boolean(challenge?.smsOptedOut)])

  const handleSwitch = async (option: IAuthChallengeFactorOption) => {
    if (!challenge || isRetrying) return
    setIsRetrying(true)
    setError(null)
    const result = await workosAuthService.switchChallengeFactor(challenge.ticket, option.factorId)
    setIsRetrying(false)
    if (result.state) {
      applyState(result.state)
      setCode('')
      setUseRecoveryCode(false)
      return
    }
    setError(result.error ?? 'Could not switch method. Please try again.')
  }

  const handleTryAgain = async () => {
    if (!challenge || isRetrying) return
    setIsRetrying(true)
    setError(null)
    const result = await workosAuthService.resendChallengeCode(challenge.ticket)
    setIsRetrying(false)
    if (result.state) {
      applyState(result.state)
      return
    }
    setError(
      result.errorCode === ErrorCode.SMS_RECIPIENT_OPTED_OUT
        ? 'Texts are still blocked. Text START first, then try again.'
        : (result.error ?? 'Could not send a code. Please try again.'),
    )
  }

  useEffect(() => {
    if (!open) return
    setChallenge(null)
    setCode('')
    setUseRecoveryCode(false)
    setError(null)
    setAlternatives([])
    startStepUpAsync()
      .then(setChallenge)
      .catch((err) => setError(getErrorMessage(err)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const handleVerify = async (submitted?: string) => {
    const value = (submitted ?? code).trim()
    if (!challenge || !value || isVerifyingStepUp) return
    setError(null)
    try {
      await verifyStepUpAsync({ ticket: challenge.ticket, code: value })
      onVerified()
    } catch (err) {
      setError(getErrorMessage(err))
      if (!useRecoveryCode) setCode('')
    }
  }

  const lastFour = challenge?.maskedPhone?.replace(/\D/g, '').slice(-4)
  // No code can arrive while the number is blocked: the code field steps aside.
  const textsBlocked = challenge?.factorType === 'sms' && Boolean(challenge?.smsOptedOut)
  const prompt = useRecoveryCode
    ? 'Enter one of your saved recovery codes.'
    : textsBlocked && challenge?.smsOptedOut
      ? challenge.smsOptedOut.message
      : challenge?.factorType === 'sms'
      ? `Enter the ${CODE_LENGTH}-digit code we texted to the phone ending in ${lastFour ?? '…'}.`
      : `Enter the ${CODE_LENGTH}-digit code from your authenticator app.`

  return (
    <Modal
      title="Confirm it's you"
      open={open}
      onClose={onClose}
      actions={
        <>
          <Button disableElevation variant="secondary" onClick={onClose} disabled={isVerifyingStepUp}>
            Cancel
          </Button>
          <Button
            disableElevation
            variant="primary"
            loading={isVerifyingStepUp}
            disabled={!challenge || !code.trim() || isVerifyingStepUp || (textsBlocked && !useRecoveryCode)}
            onClick={() => handleVerify()}
          >
            Continue
          </Button>
        </>
      }
    >
      <Stack spacing={4}>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          Before you add another verification method, confirm with the one you already have.
        </Typography>
        {isStartingStepUp || !challenge ? (
          error ? (
            <Typography variant="body3" color="error.main">
              {error}
            </Typography>
          ) : null
        ) : (
          <>
            <Typography variant="body2" sx={{ lineHeight: 1.5 }} data-testid={textsBlocked && !useRecoveryCode ? 'sms-opted-out-message' : undefined}>
              {prompt}
            </Typography>
            {textsBlocked && !useRecoveryCode ? (
              <Stack spacing={1} alignItems="flex-start">
                <Button disableElevation variant="tertiary" disabled={isRetrying} onClick={handleTryAgain}>
                  Try again
                </Button>
                {alternatives.map((option) => (
                  <Button
                    key={option.factorId}
                    disableElevation
                    variant="tertiary"
                    disabled={isRetrying || option.optedOut}
                    onClick={() => handleSwitch(option)}
                  >
                    {option.type === 'totp' ? 'Use your authenticator app instead' : 'Text a code instead'}
                  </Button>
                ))}
              </Stack>
            ) : useRecoveryCode ? (
              <TextField
                fullWidth
                autoFocus
                placeholder="XXXXX-XXXXX"
                value={code}
                onChange={(e) => {
                  setCode(e.target.value)
                  setError(null)
                }}
              />
            ) : (
              <>
                <FieldLabel label="Verification code" />
                <CodeInput
                  autoFocus
                  value={code}
                  onChange={(value) => {
                    setCode(value)
                    setError(null)
                  }}
                  onComplete={(value) => handleVerify(value)}
                  disabled={isVerifyingStepUp}
                  length={CODE_LENGTH}
                  aria-label="Verification code"
                />
              </>
            )}
            {error && (
              <Typography variant="body3" color="error.main">
                {error}
              </Typography>
            )}
            <Button
              disableElevation
              variant="tertiary"
              onClick={() => {
                setUseRecoveryCode((v) => !v)
                setCode('')
                setError(null)
              }}
            >
              {useRecoveryCode ? 'Use your verification code instead' : 'Use a recovery code instead'}
            </Button>
          </>
        )}
      </Stack>
    </Modal>
  )
}
